import './ShareProfile.sass';

import React, { FC, useCallback, useEffect, useState } from 'react';
import { useRoute } from 'react-router5';
import { useDispatch } from 'react-redux';

import { Api } from '$api';
import { Loader, MetaTags, UnitName, UnitPhoto, Verified } from '$shared/components';
import { Button, Placeholder } from '$uikit';
import { useTranslator } from '$hooks';
import { LinkModel, UserModel } from '$store/models';
import { IcUser28 } from '$assets';
import { ButtonIcons } from '$assets/mapping';
import { formatBigNumber, rnbr } from '$utils';
import { mainActions } from '$store/main';

export const ShareProfile: FC = () => {
  const { route } = useRoute();
  const t = useTranslator();
  const dispatch = useDispatch();

  const [loadingStatus, setLoadingStatus] = useState('loading');
  const [links, setLinks] = useState<LinkModel[]>([]);
  const [user, setUser] = useState<UserModel>();
  const [totalLikes, setTotalLikes] = useState(0);
  const [totalPosts, setTotalPosts] = useState(0);

  const loadData = useCallback(() => {
    Api.get(`/profile/${route.params.domain}/share_info`)
      .then((resp) => {
        const data: any = resp;
        dispatch(mainActions.setUser(data.user));
        setLinks(data.links);
        setUser(data.user);
        setTotalLikes(data.totalLikes);
        setTotalPosts(data.postsCount);
        setLoadingStatus('');
      })
      .catch(() => {
        setLoadingStatus('failed');
      });
  }, [route]);

  useEffect(() => {
    loadData();
  }, []);

  function renderContent() {
    if (loadingStatus === 'loading') {
      return <Loader isWrapped />;
    }

    if (loadingStatus === 'failed') {
      return (
        <Placeholder
          title={t('failed_title')}
          actions={
            <Button size="small" onClick={loadData}>
              {t('failed_button')}
            </Button>
          }
        >
          {t('failed_caption')}
        </Placeholder>
      );
    }

    const href = `https://ton.place/${user!.domain ? user!.domain : `id${user!.id}`}`;
    return (
      <div className="ShareProfile">
        <MetaTags
          title={user!.firstName + ' ' + user!.lastName}
          description={`${user!.status}\n${user!.bio}`}
          image={user!.photo}
        />
        <div className="ShareProfile__cont">
          <a href={href} target="_blank" rel="noreferrer">
            <UnitPhoto unit={user!} routeName={null} round size="extra-large" />
          </a>
          <div className="ShareProfile__info__cont">
            <a href={href} target="_blank" rel="noreferrer">
              <div className="ShareProfile__info__name_wrap">
                <div className="ShareProfile__info__name">
                  <UnitName unit={user!} />
                </div>
                <Verified objectId={user!.id} />
              </div>
              <div className="ShareProfile__info__bio">
                {rnbr(user!.bio || user!.status)}
              </div>
            </a>
            <div className="ShareProfile__info__buttons">
              <Button icon={<IcUser28 />} href={href} target="_blank">
                {t('share_profile_open_profile')}
              </Button>
            </div>
            <div className="ShareProfile__info__info">
              {t('share_profile_stats', {
                posts: formatBigNumber(totalPosts),
                followers: formatBigNumber(user!.followers),
                likes: formatBigNumber(totalLikes),
              })}
            </div>
          </div>
          {links.length > 0 && (
            <div className="ShareProfile__links_wrap">
              <div className="ShareProfile__links__title">
                {t('share_profile_other_social_networks')}
              </div>
              <div className="ShareProfile__links">
                {links.map((link) => {
                  const Icon = ButtonIcons[link.icon];
                  return (
                    <Button
                      key={link.id}
                      icon={link.icon === 'disable' ? undefined : <Icon />}
                      color={link.color}
                      href={link.url}
                      target="_blank"
                      mode="outline"
                    >
                      {link.text}
                    </Button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return <>{renderContent()}</>;
};
